import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { CLIENT_ERROR_MESSAGES, TOAST_TITLES } from '@/shared/config';

export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api';

const TOAST_DEBOUNCE_TIME = 1000;

export interface ApiClientHandlers {
  unauthorizedHandler: () => void;
  clearAuthStateHandler: () => void;
  tokenRefreshedHandler: () => void;
  showErrorToastHandler: (
    title: string,
    message: string,
    timeout?: number | false
  ) => void;
}

interface QueuedPromise {
  resolve: (value: null) => void;
  reject: (reason?: unknown) => void;
}

type RequestConfigWithRetry = InternalAxiosRequestConfig & { _retry?: boolean };

export function createApiClient(handlers: ApiClientHandlers): {
  api: AxiosInstance;
  resetAuthInvalidationFlag: () => void;
} {
  let isRefreshing = false;
  let isAuthInvalidated = false;
  let lastToastTime = 0;
  let failedQueue: QueuedPromise[] = [];

  const {
    unauthorizedHandler,
    clearAuthStateHandler,
    tokenRefreshedHandler,
    showErrorToastHandler,
  } = handlers;

  function notifyTokenRefreshed(): void {
    try {
      tokenRefreshedHandler();
    } catch (error) {
      console.warn('Token refresh handler failed:', error);
    }
  }

  function processQueue(refreshError: unknown): void {
    failedQueue.forEach((prom) => {
      if (refreshError) {
        prom.reject(refreshError);
      } else {
        prom.resolve(null);
      }
    });
    failedQueue = [];
  }

  function clearAuthAndRedirect(
    currentTime: number,
    errorMessage?: string
  ): void {
    isAuthInvalidated = true;
    clearAuthStateHandler();
    if (currentTime - lastToastTime > TOAST_DEBOUNCE_TIME) {
      lastToastTime = currentTime;
      showErrorToastHandler(
        TOAST_TITLES.ERROR,
        errorMessage ?? CLIENT_ERROR_MESSAGES.AUTH_SESSION_EXPIRED,
        errorMessage ? false : 4000
      );
    }
    unauthorizedHandler();
  }

  function resetAuthInvalidationFlag(): void {
    isAuthInvalidated = false;
  }

  const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
  });

  api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RequestConfigWithRetry | undefined;
      const currentTime = Date.now();

      if (isAuthInvalidated) {
        return Promise.reject(error);
      }

      if (originalRequest?.url?.includes('auth/refresh')) {
        isRefreshing = false;
        clearAuthAndRedirect(
          currentTime,
          (error.response?.data as { message?: string })?.message
        );
        throw error;
      }

      if (originalRequest?.url?.includes('auth/login')) {
        throw error;
      }

      const status = error.response?.status;
      const shouldRetry =
        status === 401 && originalRequest && !originalRequest._retry;

      if (shouldRetry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise<null>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => api.request(originalRequest));
        }

        isRefreshing = true;

        try {
          await api.get('/auth/refresh');

          notifyTokenRefreshed();

          processQueue(null);

          return api.request(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          clearAuthAndRedirect(currentTime);
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      throw error;
    }
  );

  return { api, resetAuthInvalidationFlag };
}
